import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clients as clientsApi, stats as statsApi, plans as plansApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import DashboardStats from './DashboardStats.jsx';
import { DownloadIcon, MapIcon, PlusIcon, CheckIcon, TruckIcon, NavigateIcon } from './icons.jsx';

export default function Dashboard() {
  const { user, activeCampaign, campaigns, viewAsRole } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignComplete, setCampaignComplete] = useState(false);
  const [todayPlan, setTodayPlan] = useState(null);
  const campaignEntry = campaigns.find((c) => c.campaignId === activeCampaign);
  const effectiveRole = viewAsRole || campaignEntry?.role;
  const isDriver = effectiveRole === 'driver';
  const isPartner = effectiveRole === 'partner';

  useEffect(() => {
    if (!activeCampaign) return;
    const today = new Date().toISOString().split('T')[0];
    Promise.all([
      clientsApi.list().then(setClients),
      statsApi.get().then((s) => setCampaignComplete(!!s.campaignComplete)).catch(() => {}),
      plansApi.list({ date: today }).then((plans) => {
        const active = plans.find((p) => p.status !== 'completed');
        setTodayPlan(active || null);
      }).catch(() => {}),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCampaign]);

  const totalUnits = clients.reduce((sum, c) => sum + (c.nb_units || 0), 0);
  const delivered = clients.filter(c => c.delivery_status === 'delivered');
  const deliveredUnits = delivered.reduce((sum, c) => sum + (c.nb_units || 0), 0);
  const problems = clients.filter(c => c.delivery_status === 'problem').length;
  const pending = clients.filter(c => c.delivery_status === 'pending').length;
  const progress = totalUnits > 0 ? Math.round((deliveredUnits / totalUnits) * 100) : 0;

  const campaignName = campaigns.find(c => c.campaignId === activeCampaign)?.campaignName || 'Aucune campagne';

  if (!activeCampaign) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-bold mb-3 text-[var(--color-primary)]">Bienvenue sur Velogistique</h2>
        <p className="text-[var(--color-neutral)]">Aucune campagne active. Contactez un administrateur.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-xl font-bold mb-1 text-[var(--color-primary)]">Tableau de bord</h2>
      <p className="text-sm text-[var(--color-neutral)] mb-6">{campaignName}</p>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-3 border-gray-200 border-t-[var(--color-accent)] rounded-full animate-spin mx-auto" />
          <p className="text-[var(--color-neutral)] mt-3 text-sm">Chargement...</p>
        </div>
      ) : isDriver ? (
        /* ====== DASHBOARD LIVREUR SIMPLIFIE ====== */
        <div className="space-y-5">
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
            <p className="text-lg font-bold text-[#0f172a] mb-1">Bonjour {user?.name?.split(' ')[0] || 'Livreur'}</p>
            <p className="text-sm text-[#64748b]">{campaignName}</p>
          </div>

          {todayPlan ? (
            <>
              <div className="bg-gradient-to-br from-[#0f172a] to-[#1e40af] rounded-2xl p-6 text-white text-center">
                <p className="text-white/70 text-sm mb-2">Tournee du jour</p>
                <p className="text-3xl font-bold">{todayPlan.stop_count || '?'} arrets</p>
                <p className="text-white/70 text-sm mt-1">{todayPlan.total_units || '?'} unites a livrer</p>
                {todayPlan.delivered_count > 0 && (
                  <div className="mt-3">
                    <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-400 rounded-full" style={{ width: `${Math.round((todayPlan.delivered_count / todayPlan.stop_count) * 100)}%` }} />
                    </div>
                    <p className="text-xs text-white/60 mt-1">{todayPlan.delivered_count}/{todayPlan.stop_count} termines</p>
                  </div>
                )}
              </div>
              <Link
                to={`/delivery/${todayPlan.id}`}
                className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#059669] to-emerald-500 text-white rounded-2xl p-5 font-bold text-lg shadow-lg shadow-emerald-500/20 min-h-[64px]"
              >
                <TruckIcon size={24} />
                {todayPlan.delivered_count > 0 ? 'Reprendre la tournee' : 'Demarrer la tournee'}
              </Link>
            </>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <p className="text-4xl mb-3">📋</p>
              <p className="text-[#0f172a] font-medium">Pas de tournee prevue aujourd'hui</p>
              <p className="text-sm text-[#64748b] mt-1">Contactez votre coordinateur</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Link to="/map" className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#0f172a] rounded-xl p-4 font-medium text-sm">
              <MapIcon size={16} /> Carte
            </Link>
            <Link to="/chat" className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#0f172a] rounded-xl p-4 font-medium text-sm">
              💬 Chat
            </Link>
          </div>
        </div>
      ) : (
        <>
          {campaignComplete && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 mb-5">
              <CheckIcon size={20} className="text-emerald-600 shrink-0" />
              <span className="font-medium text-sm">Campagne terminée -- toutes les unités sont livrées !</span>
            </div>
          )}

          {/* Hero progress card */}
          <div className="bg-gradient-to-br from-[#0f172a] to-[#1e40af] rounded-xl p-6 mb-5 text-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-white/70 text-sm mb-1">Progression campagne</p>
                <p className="text-4xl font-bold">{progress}%</p>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs">{deliveredUnits} / {totalUnits} unités</p>
                <p className="text-white/70 text-xs">{delivered.length} / {clients.length} clients</p>
              </div>
            </div>
            <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-400 rounded-full animate-progress"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <DashboardStats
            total={clients.length}
            delivered={delivered.length}
            pending={pending}
            problems={problems}
          />

          {/* Start delivery - prominent CTA */}
          {todayPlan && (
            <Link
              to={`/delivery/${todayPlan.id}`}
              className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#059669] to-emerald-500 hover:from-[#047857] hover:to-emerald-600 text-white rounded-xl p-5 font-bold text-base shadow-lg shadow-emerald-500/20 mt-5 min-h-[56px]"
            >
              <TruckIcon size={22} />
              <span>Lancer la tournée du jour</span>
            </Link>
          )}

          {/* Quick actions - cachees pour partner (read-only scope) */}
          {!isPartner && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Link
                to="/plans/suggest"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#1e40af] to-[#0891b2] hover:opacity-90 text-white rounded-xl p-4 font-medium shadow-sm text-sm"
              >
                <NavigateIcon size={16} />
                <span>Itinéraire auto</span>
              </Link>
              <Link
                to="/import"
                className="flex items-center justify-center gap-2 bg-[var(--color-orange)] hover:opacity-90 text-white rounded-xl p-4 font-medium shadow-sm text-sm"
              >
                <DownloadIcon size={16} />
                <span>Importer</span>
              </Link>
              <Link
                to="/map"
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-[var(--color-primary)] hover:bg-gray-50 rounded-xl p-4 font-medium text-sm"
              >
                <MapIcon size={16} />
                <span>Carte</span>
              </Link>
              <Link
                to="/plans/new"
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-[var(--color-primary)] hover:bg-gray-50 rounded-xl p-4 font-medium text-sm"
              >
                <PlusIcon size={16} />
                <span>Nouvelle tournée</span>
              </Link>
            </div>
          )}
          {isPartner && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Link
                to="/clients"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#1e40af] to-[#0891b2] text-white rounded-xl p-4 font-medium shadow-sm text-sm"
              >
                <span>Voir mes clients</span>
              </Link>
              <Link
                to="/map"
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-[var(--color-primary)] rounded-xl p-4 font-medium text-sm"
              >
                <MapIcon size={16} />
                <span>Carte</span>
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
